import CreditCardList from "@/models/api/administration/bankAccounts/CreditCardList";
import { moduleActionContext } from "@/store";
import { defineModule } from "direct-vuex";
import {
  creditCardLock,
  creditCardUnlock,
  getCreditCardsPaged,
  creditCardAdd,
  creditCardRemove,
  creditCardEdit,
  getCreditCard,
} from "@/requests/administration/administrationCreditCards";
import { FILTER_NAMES } from "@/static/filterNames";
import _ from "lodash";
import { CriterionBase } from "@/models/api/filters/criterions/CriterionBase";
import { DataOptions } from "vuetify";
import { DefaultDataOptions } from "@/models/components/dataTables/DataTableOptions";
import CreditCard, { CreditCardAddEdit } from "@/models/api/administration/bankAccounts/CreditCard";
import { ConfirmModel } from "@/models/components/popup/Confirm";
import { filterCapitalize } from "@/filters/capitalize";
import i18n from "@/plugins/i18n";
import { bold } from "@/filters/bold";

export interface AdministrationCreditCardsStateInterface {
  creditCards: Array<CreditCard>;
  creditCardsCount: number | null;
  selectedCreditCardId: number | null;
  creditCardsFilters: null | Array<CriterionBase>;
  creditCardsDataTableOptions: DataOptions;
  recordStampId: null | number;
  creditCardAddEdit: CreditCardAddEdit;
  isAddCreditCardDialog: boolean;
  isEditDialogFlag: boolean;
}

export const AdministrationCreditCardsState: AdministrationCreditCardsStateInterface = {
  creditCards: [],
  creditCardsCount: null,
  selectedCreditCardId: null,
  creditCardsFilters: null,
  creditCardsDataTableOptions: new DefaultDataOptions(),
  recordStampId: null,
  creditCardAddEdit: new CreditCardAddEdit(),
  isAddCreditCardDialog: false,
  isEditDialogFlag: false,
};

const AdministrationCreditCardsModule = defineModule({
  state: AdministrationCreditCardsState as AdministrationCreditCardsStateInterface,
  namespaced: true as const,
  getters: {},
  mutations: {
    setCreditCards(state, param: CreditCardList) {
      state.creditCards = param.items;
      state.creditCardsCount = param.totalCount;
    },
    setSelectedCreditCard(state, param: number) {
      state.selectedCreditCardId = param;
    },
    setCreditCardsDataTableOptions(state, param: DataOptions) {
      state.creditCardsDataTableOptions = param;
    },
    setCreditCardsFilters(state, param: Array<CriterionBase>) {
      state.creditCardsFilters = param;
    },
    setRecordStampId(state, param: number | null) {
      state.recordStampId = param;
    },
    setIsAddNewCreditCardDialog(state, param: boolean) {
      state.isAddCreditCardDialog = param;
    },
    clearNewCreditCard(state) {
      state.creditCardAddEdit = new CreditCardAddEdit();
    },
    setCreditCardNew(state, param: CreditCardAddEdit) {
      state.creditCardAddEdit = param;
    },
    setIsEditDialogFlag(state, param: boolean) {
      state.isEditDialogFlag = param;
    },
  },
  actions: {
    async addNewCreditCard(context) {
      const { rootDispatch, state, commit, dispatch } = _moduleActionContext(context);
      const newCard = state.creditCardAddEdit;
      await creditCardAdd(newCard);
      rootDispatch.popup.addOperationSuccessfulSnackbar();
      commit.setIsAddNewCreditCardDialog(false);
      commit.clearNewCreditCard();
      dispatch.downloadCreditCardPaged();
    },
    async downloadCreditCardPaged(context) {
      const { commit, state } = _moduleActionContext(context);
      const creditCards = await getCreditCardsPaged({
        count: state.creditCardsDataTableOptions.itemsPerPage,
        page: state.creditCardsDataTableOptions.page - 1,
        criterions: _.cloneDeep(state.creditCardsFilters),
        filterName: FILTER_NAMES.CreditCardsFilter,
      });
      commit.setCreditCards(creditCards);
    },

    async showEditDialog(context, itemId: number) {
      const { dispatch, commit } = _moduleActionContext(context);
      dispatch.getCreditCardById(itemId);
      commit.setIsEditDialogFlag(true);
      commit.setIsAddNewCreditCardDialog(true);
    },

    async getCreditCardById(context, itemId: number) {
      const { commit } = _moduleActionContext(context);
      const creditCard = await getCreditCard(itemId);
      commit.setCreditCardNew(creditCard);
    },

    async editCreditCard(context) {
      const { rootDispatch, dispatch, state, commit } = _moduleActionContext(context);
      const creditCardToEdit = state.creditCardAddEdit;
      if (creditCardToEdit) {
        const isConfirmed = (await rootDispatch.popup.addConfirm(
          new ConfirmModel(
            filterCapitalize(
              i18n.t("doYouReallyWantToEditCreditCard", {
                creditCardName: bold(creditCardToEdit.name),
                number: bold(creditCardToEdit.number),
              })
            ),
            filterCapitalize(i18n.t("warning")),
            [
              {
                text: filterCapitalize(i18n.t("cancel")),
                color: "warning",
                value: false,
                outlined: true,
              },
              {
                text: filterCapitalize(i18n.t("confirm")),
                color: "warning",
                value: true,
              },
            ],
            "550px"
          )
        )) as boolean;
        if (isConfirmed) {
          await creditCardEdit(creditCardToEdit);
          rootDispatch.popup.addOperationSuccessfulSnackbar();
          dispatch.downloadCreditCardPaged();
        }
      }
      commit.clearNewCreditCard();
      commit.setIsAddNewCreditCardDialog(false);
    },

    async removeCreditCard(context, creditCard: CreditCard) {
      const { rootDispatch, dispatch } = _moduleActionContext(context);

      const isConfirmed = (await rootDispatch.popup.addConfirm(
        new ConfirmModel(
          filterCapitalize(
            i18n.t("doYouReallyWantToRemoveCreditCard", {
              creditCardNumber: bold(creditCard.number),
              creditCardName: bold(creditCard.name),
            })
          ),
          filterCapitalize(i18n.t("warning")),
          [
            {
              text: filterCapitalize(i18n.t("cancel")),
              color: "warning",
              value: false,
              outlined: true,
            },
            {
              text: filterCapitalize(i18n.t("confirm")),
              color: "warning",
              value: true,
            },
          ],
          "550px"
        )
      )) as boolean;

      if (isConfirmed) {
        await creditCardRemove(creditCard.id);
        rootDispatch.popup.addOperationSuccessfulSnackbar();
      }
      dispatch.downloadCreditCardPaged();
    },

    async lockCreditCard(context, creditCard: CreditCard) {
      const { rootDispatch, dispatch } = _moduleActionContext(context);

      const isConfirmed = (await rootDispatch.popup.addConfirm(
        new ConfirmModel(
          filterCapitalize(
            i18n.t("doYouReallyWantToLockCreditCard", {
              creditCardNumber: bold(creditCard.number),
              creditCardName: bold(creditCard.name),
            })
          ),
          filterCapitalize(i18n.t("warning")),
          [
            {
              text: filterCapitalize(i18n.t("cancel")),
              color: "warning",
              value: false,
              outlined: true,
            },
            {
              text: filterCapitalize(i18n.t("confirm")),
              color: "warning",
              value: true,
            },
          ],
          "550px"
        )
      )) as boolean;

      if (isConfirmed) {
        await creditCardLock(creditCard.id);
        rootDispatch.popup.addOperationSuccessfulSnackbar();
      }
      dispatch.downloadCreditCardPaged();
    },
    async unlockCreditCard(context, creditCard: CreditCard) {
      const { rootDispatch, dispatch } = _moduleActionContext(context);

      const isConfirmed = (await rootDispatch.popup.addConfirm(
        new ConfirmModel(
          filterCapitalize(
            i18n.t("doYouReallyWantToUnlockCreditCard", {
              creditCardNumber: bold(creditCard.number),
              creditCardName: bold(creditCard.name),
            })
          ),
          filterCapitalize(i18n.t("warning")),
          [
            {
              text: filterCapitalize(i18n.t("cancel")),
              color: "warning",
              value: false,
              outlined: true,
            },
            {
              text: filterCapitalize(i18n.t("confirm")),
              color: "warning",
              value: true,
            },
          ],
          "550px"
        )
      )) as boolean;

      if (isConfirmed) {
        await creditCardUnlock(creditCard.id);
        rootDispatch.popup.addOperationSuccessfulSnackbar();
      }
      dispatch.downloadCreditCardPaged();
    },
  },
});

export default AdministrationCreditCardsModule;
// eslint-disable-next-line
const _moduleActionContext = (context: any) => moduleActionContext(context, AdministrationCreditCardsModule);
